// src/components/WhiteScreenDetector2.tsx

import { useEffect } from "react";
import { errorTracking } from '../utils/errorTracking';
import { isWidgetOpened } from '../utils/widgetOpenedGate';

type ReportFn = (params: Record<string, any>) => void;

// ここはあなたの Analytics 送信関数に置き換え
const reportWhiteScreen: ReportFn = (params) => {
  try {
    // 先読み中(非表示 iframe)はそもそも画面が見えないので白画面扱いしない（誤検知防止）
    if (!isWidgetOpened()) return;
    // Firebase Analyticsにも送信
    errorTracking.logCustomError('White screen detected (advanced)', params);

    const url = import.meta.env.VITE_WS_DETECT_INGEST_URL || "";
    if (!url) return;
    
    const body = JSON.stringify({
      e: "white_screen_detected",
      ...params,
      href: window.location.href,
      ua: navigator.userAgent,
      release: import.meta.env.VITE_VERSION || 'unknown',
      env: import.meta.env.MODE,
      t: new Date().toISOString(),
    });
    
    if ("sendBeacon" in navigator) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body });
    }
  } catch (error) {
    console.warn('Failed to report white screen:', error);
  }
};

// デバイス情報の取得
const getDeviceInfo = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  let osVersion: string | undefined;
  if (isIOS) {
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (match) {
      osVersion = `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}`;
    }
  }

  return {
    is_ios: isIOS,
    is_safari: isSafari,
    os_version: osVersion,
    device_pixel_ratio: window.devicePixelRatio,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    window_width: window.innerWidth,
    window_height: window.innerHeight,
    memory_js_mb: (performance as any).memory?.usedJSHeapSize
      ? Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024)
      : null,
    memory_limit_mb: (performance as any).memory?.jsHeapSizeLimit
      ? Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
      : null,
    connection_type: (navigator as any).connection?.effectiveType || 'unknown',
    is_online: navigator.onLine,
    visibility_state: document.visibilityState
  };
};

export default function WhiteScreenDetector2() {
  useEffect(() => {
    let painted = false;
    let domChanged = false;
    let timeoutId: number | null = null;
    let lcpObserver: PerformanceObserver | null = null;
    let paintObserver: PerformanceObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const deviceInfo = getDeviceInfo();
    const startTime = performance.now();

    // Paint Timing: FCP/LCP（対応環境でのみ）
    try {
      if ("PerformanceObserver" in window) {
        // First Contentful Paint の監視
        paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === "first-contentful-paint") {
              painted = true;
            }
          }
        });
        paintObserver.observe({ type: "paint", buffered: true });

        // Largest Contentful Paint の監視
        lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            // LCPが検出されたら描画は成功している
            painted = true;
          }
        });
        lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
      }
    } catch (error) {
      console.warn('Performance observer initialization failed:', error);
    }

    // DOM変化監視（全く要素が増えない＝描画止まり）
    try {
      mutationObserver = new MutationObserver((mutations) => {
        // 意味のある変更かチェック
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // テキストノードや要素が追加された
            for (let i = 0; i < mutation.addedNodes.length; i++) {
              const node = mutation.addedNodes[i];
              if (node.nodeType === Node.ELEMENT_NODE || 
                  (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())) {
                domChanged = true;
                break;
              }
            }
          }
        }

        // body に子要素があるかチェック
        if (document.body && document.body.childElementCount > 0) {
          domChanged = true;
        }
      });

      if (document.body) {
        mutationObserver.observe(document.body, { 
          childList: true, 
          subtree: true,
          characterData: true 
        });
      } else {
        // body がまだ無い場合はdocument全体を監視
        mutationObserver.observe(document, { 
          childList: true, 
          subtree: true 
        });
      }
    } catch (error) {
      console.warn('Mutation observer initialization failed:', error);
    }

    // 一定時間待っても描画兆候が無ければ報告
    timeoutId = window.setTimeout(() => {
      const waitedMs = Math.round(performance.now() - startTime);
      
      // 描画状況の詳細チェック
      const bodyExists = !!document.body;
      const bodyHasChildren = document.body ? document.body.childElementCount > 0 : false;
      const headHasChildren = document.head ? document.head.childElementCount > 0 : false;
      const totalElements = document.querySelectorAll('*').length;
      
      // 白画面の種類を分類
      let whiteScreenType = 'unknown';
      if (!painted && !domChanged) {
        whiteScreenType = 'complete_failure'; // 最も深刻
      } else if (!painted && domChanged) {
        whiteScreenType = 'render_failure'; // DOMはあるがレンダリング失敗
      } else if (painted && !domChanged) {
        whiteScreenType = 'content_failure'; // 初期描画はあるがコンテンツなし
      }

      // エラー情報の収集
      const errorDetails = {
        phase: "first_paint_timeout",
        white_screen_type: whiteScreenType,
        waited_ms: waitedMs,
        painted_fcp: painted,
        dom_changed: domChanged,
        body_exists: bodyExists,
        body_has_children: bodyHasChildren,
        head_has_children: headHasChildren,
        total_elements: totalElements,
        ...deviceInfo,
        // JavaScript エラーがあったかチェック
        js_errors_detected: (window as any).__jsErrorCount > 0,
        // ネットワークエラーがあったかチェック
        network_errors_detected: (window as any).__networkErrorCount > 0,
        // CSS読み込み状況
        stylesheets_count: document.styleSheets.length,
        // スクリプト読み込み状況
        scripts_count: document.scripts.length,
        // 現在のURL情報
        current_pathname: window.location.pathname,
        current_search: window.location.search.substring(0, 100), // 最初の100文字のみ
        // リソース読み込み状況
        resource_timing_entries: performance.getEntriesByType('resource').length
      };

      // 白画面として報告（分類によって重要度を調整）
      if (whiteScreenType === 'complete_failure' || 
          (whiteScreenType === 'render_failure' && deviceInfo.is_ios)) {
        reportWhiteScreen(errorDetails);
        
        // iOSの場合は特別にマーク
        if (deviceInfo.is_ios) {
          errorTracking.logCustomError('Critical iOS white screen', {
            ...errorDetails,
            severity: 'critical'
          });
        }
      } else if (whiteScreenType !== 'unknown') {
        // その他の白画面も記録（重要度は低め）
        reportWhiteScreen({
          ...errorDetails,
          severity: 'warning'
        });
      }

    }, 6000); // 6秒は実運用で良い妥協点。必要に応じて調整。

    // 短期間での早期検出（3秒）- 完全な失敗のみ
    const earlyTimeoutId = window.setTimeout(() => {
      if (!painted && !domChanged && deviceInfo.is_ios) {
        const waitedMs = Math.round(performance.now() - startTime);
        reportWhiteScreen({
          phase: "early_detection",
          white_screen_type: "critical_ios_failure",
          waited_ms: waitedMs,
          painted_fcp: false,
          dom_changed: false,
          severity: 'critical',
          ...deviceInfo
        });
      }
    }, 3000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (earlyTimeoutId) clearTimeout(earlyTimeoutId);
      
      paintObserver?.disconnect();
      lcpObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  return null;
}

// グローバルエラーカウンターの初期化
if (typeof (window as any).__jsErrorCount === 'undefined') {
  (window as any).__jsErrorCount = 0;
  (window as any).__networkErrorCount = 0;
  
  // JavaScript エラーをカウント
  window.addEventListener('error', () => {
    (window as any).__jsErrorCount++;
  });
  
  window.addEventListener('unhandledrejection', () => {
    (window as any).__jsErrorCount++;
  });
}
